import { ChevronDownIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Flex,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Text,
  useToast,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import React, { useEffect, useState } from 'react';

import { Util } from '@finley-technologies/finley-types';

import { colors } from 'packages/common-frontend/colors';
import {
  errorToastConfig,
  successToastConfig,
} from 'packages/common-frontend/constants';
import { useCreditFacilityContext } from 'packages/credit-facility/hooks/CreditFacilityProvider';
import { CreditFacilitySelect } from 'packages/credit-facility/ui/CreditFacilitySelect';
import {
  useCreateAssignmentServicerReportPreviewMutation,
  useGetCompletedReceivableResponseTapePreviewIdLazyQueryGQLType,
} from 'packages/loanProduct/requests/requests.gql.generated';

export const EmailServicerReportPreviewsButton = ({
  creditFacilities,
  fileVersion,
  reportingDate,
  fileNamePrefix,
  previewIdentifier,
}: {
  creditFacilities: { id: string; slug: string }[];
  fileVersion: number;
  reportingDate: Util.FullDateStr;
  fileNamePrefix: string;
  previewIdentifier: string;
}) => {
  const toast = useToast();

  const { creditFacilityId, setCreditFacilityId } = useCreditFacilityContext();

  const [fileName, setFileName] = useState<string>();
  const [
    completedReceivableResponseTapePreviewId,
    setCompletedReceivableResponseTapePreviewId,
  ] = useState<string>();

  const [
    getCompletedReceivableResponseTapePreviewId,
    { loading: completedPreviewIdIsLoading },
  ] = useGetCompletedReceivableResponseTapePreviewIdLazyQueryGQLType({
    onError: () => {
      errorToastConfig.title = `Unable to find completed receivable response tape preview`;
      toast(errorToastConfig);
    },
    onCompleted: (response) => {
      if (response && response.completedReceivableResponseTapePreviewId) {
        setCompletedReceivableResponseTapePreviewId(
          response.completedReceivableResponseTapePreviewId,
        );
      }
    },
  });

  useEffect(() => {
    const selectedCreditFacility = creditFacilities.find(
      (cf) => cf.id === creditFacilityId,
    );
    if (!selectedCreditFacility && creditFacilities.length > 0) {
      setCreditFacilityId(creditFacilities[0].id);
    } else {
      getCompletedReceivableResponseTapePreviewId({
        variables: {
          creditFacilityId,
          fileVersion,
          reportingDate,
          previewIdentifier,
        },
      });
    }
  }, [
    creditFacilityId,
    setCreditFacilityId,
    fileVersion,
    reportingDate,
    previewIdentifier,
    getCompletedReceivableResponseTapePreviewId,
    creditFacilities,
  ]);

  const [
    createAssignmentServicerReportPreview,
  ] = useCreateAssignmentServicerReportPreviewMutation({
    onCompleted: () => {
      successToastConfig.title =
        'Creating servicing report preview from assignment proposals, please check your email in a few minutes.';
      toast(successToastConfig);
    },
    onError: () => {
      errorToastConfig.title = `Error creating servicing report preview from assignment proposals.`;
      toast(errorToastConfig);
    },
  });

  const createAndEmailServicerReportPreviewCallback = async (cfId: string) => {
    if (completedReceivableResponseTapePreviewId && fileName) {
      createAssignmentServicerReportPreview({
        variables: {
          assignmentServicerReportPreviewInput: {
            creditFacilityId: cfId,
            reportingDate,
            fileVersion,
            servicerReportPreviewType: 'assignmentServicerReportPreview',
            receivableResponseTapePreviewId: completedReceivableResponseTapePreviewId,
            previewIdentifier,
          },
        },
      });
      Sentry.addBreadcrumb({
        category: 'ui.click',
        message: `Clicked email servicer report preview from assignment ${fileName}`,
        level: Sentry.Severity.Info,
      });
    }
  };

  useEffect(() => {
    const selectedCreditFacility = creditFacilities.find(
      (creditFacility) => creditFacility.id === creditFacilityId,
    );
    if (selectedCreditFacility) {
      setFileName(
        `${fileNamePrefix}-${selectedCreditFacility.slug}-${reportingDate}-v${fileVersion}`,
      );
    }
  }, [
    creditFacilities,
    creditFacilityId,
    fileNamePrefix,
    fileVersion,
    reportingDate,
  ]);

  return (
    <Popover placement="bottom-start">
      <PopoverTrigger>
        <Button
          borderRadius="8px"
          colorScheme="aurora"
          variant="outline"
          border="2px solid"
          rightIcon={<ChevronDownIcon />}
        >
          Email Pro Forma Report
        </Button>
      </PopoverTrigger>
      <PopoverContent
        borderRadius="4px"
        padding="24px"
        background={colors.background[50]}
        width="auto"
      >
        <Text fontWeight="600">Email Pro Forma Report</Text>
        <Box m="32px 0px" background="white">
          <CreditFacilitySelect
            creditFacilitySlugFilter={(creditFacilitySlug) =>
              creditFacilities.some(
                (creditFacility) => creditFacility.slug === creditFacilitySlug,
              )
            }
          />
        </Box>
        <Flex direction="row-reverse">
          <Button
            borderRadius="8px"
            colorScheme="aurora"
            border="2px solid"
            onClick={() => {
              createAndEmailServicerReportPreviewCallback(creditFacilityId);
            }}
            isDisabled={
              completedPreviewIdIsLoading ||
              !completedReceivableResponseTapePreviewId
            }
          >
            Email
          </Button>
        </Flex>
      </PopoverContent>
    </Popover>
  );
};
